// Hold-ground laser turret (Gate 2 feature).
// When the player holds position, a turret deploys: the manual laser cannon
// gets a damage/fire-rate buff (consumed by the weapon system via
// getTurretWeaponBuffs) and the turret auto-fires a low-power beam at the
// nearest enemy within beam range. Fully deterministic: deployment,
// retraction, and firing all run on the fixed sim tick — no wall clock, no RNG.

import type { WorldState } from './state.js';
import type { Enemy, Vec2 } from '../types.js';
import { wrapArena, wrappedDelta } from './collision.js';
import { getSerenityMods } from './serenity.js';

/** Player speed (u/s) below which the turret starts deploying. */
export const TURRET_SPEED_THRESHOLD = 0.5;
/** Still time required to fully deploy (0.75 s at 60 Hz). */
export const TURRET_DEPLOY_TICKS = 45;
/** Retract time after moving (0.2 s at 60 Hz). */
export const TURRET_RETRACT_TICKS = 12;
/** Auto-fire interval (1.5 s at 60 Hz). */
export const TURRET_FIRE_INTERVAL_TICKS = 90;
/** Low-power auto beam stats. */
export const TURRET_BEAM_DAMAGE = 5;
export const TURRET_BEAM_SPEED = 40;
export const TURRET_BEAM_RANGE = 24;
export const TURRET_BEAM_HITBOX = 0.25;
/** Projectile weapon_id tag for turret beams (no content def; hit resolution
 * only needs the string for events). */
export const TURRET_BEAM_WEAPON_ID = 'turret.beam';
/** Deployed turret buffs applied to the manual laser cannon. */
export const TURRET_DAMAGE_MULT = 1.5;
export const TURRET_RATE_MULT = 1.5;

export interface TurretState {
  /** True once deploy_progress reaches 1; false as soon as the player moves. */
  deployed: boolean;
  /** 0..1: fills over TURRET_DEPLOY_TICKS while still, drains over
   * TURRET_RETRACT_TICKS while moving. Drives the visual scale. */
  deploy_progress: number;
  /** Sim tick of the last auto-beam shot. */
  last_shot_tick: number;
}

export function createTurretState(): TurretState {
  return { deployed: false, deploy_progress: 0, last_shot_tick: 0 };
}

/** Buffs the manual laser cannon should apply while the turret is deployed. */
export function getTurretWeaponBuffs(world: WorldState): { damageMult: number; rateMult: number } {
  if (world.turret.deployed) {
    return { damageMult: TURRET_DAMAGE_MULT, rateMult: TURRET_RATE_MULT };
  }
  return { damageMult: 1, rateMult: 1 };
}

function playerSpeed(world: WorldState): number {
  const v = world.player.vel;
  return Math.sqrt(v.x * v.x + v.y * v.y);
}

/** Nearest living, targetable enemy to the player (toroidal distance) within
 * `range`; returns null when nothing is in reach so the turret holds fire
 * instead of launching beams that expire long before they arrive.
 * Deterministic: ties break on pool order (enemies iterate in pool order). */
function nearestEnemy(world: WorldState, tick: number, range: number): Enemy | null {
  let best: Enemy | null = null;
  let bestDistSq = range * range;
  for (const e of world.enemies.alive()) {
    if (!e.targetable) continue;
    if (tick < e.telegraph_until) continue;
    const d = wrappedDelta(e.pos, world.player.pos);
    const distSq = d.x * d.x + d.y * d.y;
    if (distSq < bestDistSq) {
      bestDistSq = distSq;
      best = e;
    }
  }
  return best;
}

/** Fire one low-power beam through the shared projectile pool. */
function fireBeam(world: WorldState, target: Enemy, tick: number): void {
  const p = world.projectiles.acquire();
  if (!p) {
    world.events.emit('pool_exhausted', { kind: 'projectile' }, tick);
    return;
  }
  const d = wrappedDelta(target.pos, world.player.pos);
  const dist = Math.sqrt(d.x * d.x + d.y * d.y);
  const dir: Vec2 = dist > 1e-8 ? { x: d.x / dist, y: d.y / dist } : { x: 0, y: 1 };

  // Spawn at the hull edge, mirroring weapon-system projectiles.
  const muzzle = world.player.hitbox_radius + 0.3;
  p.pos = { x: world.player.pos.x + dir.x * muzzle, y: world.player.pos.y + dir.y * muzzle };
  wrapArena(p.pos);
  p.vel = { x: dir.x * TURRET_BEAM_SPEED, y: dir.y * TURRET_BEAM_SPEED };
  p.hitbox_radius = TURRET_BEAM_HITBOX;
  p.faction = 'ally';
  p.weapon_id = TURRET_BEAM_WEAPON_ID;
  p.damage_snapshot = TURRET_BEAM_DAMAGE * getSerenityMods(world).damageMult;
  p.penetration_left = 1;
  p.ricochet_left = 0;
  p.spawn_tick = tick;
  p.alive = true;
  p.targetable = false;
  p.hit_ids = [];
  p.expire_tick = tick + Math.max(1, Math.ceil((TURRET_BEAM_RANGE / TURRET_BEAM_SPEED) * 60));

  world.events.emit(
    'spawn',
    { kind: 'projectile', id: p.id, weapon_id: TURRET_BEAM_WEAPON_ID, pos: p.pos },
    tick
  );
}

/**
 * Per-tick turret update. Call once per sim step after player movement.
 * Currently invoked from updateEnemies (enemy-ai.ts) so game.ts needs no new
 * call site; may be moved into game.ts stepSimulation by the integrator.
 */
export function updateTurret(world: WorldState, tick: number): void {
  const turret = world.turret;
  const holding = world.player.alive && playerSpeed(world) < TURRET_SPEED_THRESHOLD;

  if (holding) {
    turret.deploy_progress = Math.min(1, turret.deploy_progress + 1 / TURRET_DEPLOY_TICKS);
    if (!turret.deployed && turret.deploy_progress >= 1) {
      turret.deployed = true;
      // First auto shot lands one interval after deployment completes.
      turret.last_shot_tick = tick;
    }
  } else {
    turret.deployed = false;
    turret.deploy_progress = Math.max(0, turret.deploy_progress - 1 / TURRET_RETRACT_TICKS);
  }

  if (turret.deployed && tick - turret.last_shot_tick >= TURRET_FIRE_INTERVAL_TICKS) {
    const target = nearestEnemy(world, tick, Math.min(TURRET_BEAM_RANGE, world.viewRadius));
    if (target) {
      fireBeam(world, target, tick);
      turret.last_shot_tick = tick;
    }
  }
}
